// Subscriptions data for the operator console (mockup 45 — "Suscripciones").
// The INHERTA business pulse: MRR/ARR, plan distribution, invoices for the period,
// new clients, churn and the next billing run. Admin-only (need-to-know).
//
// REAL (Phase 3a.6): GET /admin/subscriptions aggregates the billing tables
// cross-tenant on the admin pool, behind `billing.read.all`. Figures we cannot
// compute honestly are absent rather than invented — we store no revenue
// history, so there are no month-over-month deltas.

import { apiClient } from '../../api/client.js'

export interface RevenueKpi {
  id: string
  label: string
  value: string
  unit?: string
  caption?: { text: string; tone: 'up' | 'warn' | 'muted' }
}

/** A tier key from the `tiers` table — data-driven, so any string. */
export type PlanKey = string
export interface PlanBreakdown {
  key: PlanKey
  badge: string
  name: string
  meta: string
  clients: number
  mrr: string
  perUnit: string
  mrrPct: number
}

export type InvoiceStatus = 'paid' | 'due' | 'overdue' | 'draft'
export interface Invoice {
  id: string
  /** Deep link target: /admin/tenants/:tenantId?tab=billing */
  tenantId: string
  /** Money was actually taken — the only rows that can be refunded. */
  paid: boolean
  name: string
  initials: string
  plan: string
  folio: string
  amount: string
  overdue: boolean
  status: InvoiceStatus
  statusLabel: string
}

export interface MovementRow {
  id: string
  name: string
  initials: string
  meta: string
  amount: string
  muted?: boolean
}

export interface NextRun {
  date: string
  meta: string
  amount: string
}

/** WHO is behind, not just how many — the API has sent this since 3a and the
 *  page dropped it on the floor for months. */
export interface PastDueRow {
  tenantId: string
  name: string
  initials: string
  /** e.g. "$15,000/mes" */
  amount: string
  daysPastDue: number
  folio: string | null
  /** Past the suspension threshold: the client's agent is already stopped. */
  botStopped: boolean
}

export interface SubscriptionsData {
  period: string
  kpis: RevenueKpi[]
  plansCaption: string
  plans: PlanBreakdown[]
  invoices: Invoice[]
  pastDue: PastDueRow[]
  newClients: MovementRow[]
  churn: MovementRow[]
  /** Null until at least one active subscription has a period end. */
  nextRun: NextRun | null
}

/**
 * GET /admin/subscriptions — cross-tenant billing aggregate (MRR/ARR, plans,
 * invoices, past-due, churn), behind `billing.read.all`. Figures with no
 * honest source (e.g. MoM deltas) are absent, not invented.
 */
export async function getSubscriptions(): Promise<SubscriptionsData> {
  const res = await apiClient.get<{ data: SubscriptionsData }>('/admin/subscriptions')
  return res.data.data
}

/** Moves real money back — admins only (subscription.approve); the server
 *  refuses everyone else. Blank amount = full invoice. */
export async function refundInvoice(
  tenantId: string,
  invoiceId: string,
  input: { reason?: string; amountMxn?: number },
): Promise<void> {
  await apiClient.post(`/admin/tenants/${encodeURIComponent(tenantId)}/subscription/refund`, {
    invoiceId,
    ...input,
  })
}
